/**
 * PostModerationQueuePage (16415, 16414)
 *
 * Admin-only post (feed item / journal) moderation, in two tabs:
 * - Reported (16415): posts other users have flagged, triaged with a resolution outcome.
 * - All posts (16414): every post, filterable by moderation state, with hide/unhide/remove
 *   and a full audit trail. Consolidated into this app by work item 16532 — it previously
 *   lived as a SEPARATE page in ForSuccessFrontend (the consumer bundle), which put an
 *   admin-only surface in the wrong codebase. ChellessAdmin is the admin application; this
 *   is the only place either tab now lives.
 *
 * AC — Admin can Hide / Unhide / Remove a post; reflected immediately in row state.
 * AC — Admin can Resolve a report with outcome: NoAction | Hidden | Deleted.
 * AC — Page is admin-gated (enforced by AdminGate in App.tsx; this page trusts it has
 *      already passed).
 *
 * States handled: loading, empty, error, paginated data — for both tabs independently.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HeldWinsPanel } from './HeldWinsPanel';
import { useLatestRequest } from '../../hooks/useLatestRequest';
import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  Eye,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  getReportedPosts,
  resolvePostReport,
  hidePost,
  unhidePost,
  getModerationQueue,
  removePost,
} from '../../services/adminPostModerationService';
import type {
  ReportedPostRow,
  ReportOutcome,
  ReportStatus,
  ModeratedPostRow,
  PostModerationState,
} from '../../types/post';

type Tab = 'reported' | 'all' | 'held';

// ---------------------------------------------------------------------------
// Reported-posts tab (16415) — state model
// ---------------------------------------------------------------------------

interface QueueState {
  rows: ReportedPostRow[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  statusFilter: ReportStatus | undefined;
  loading: boolean;
  error: string | null;
  /** Per-row action in-flight: rowId → true */
  actionPending: Record<string, boolean>;
}

type QueueAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; rows: ReportedPostRow[]; totalCount: number }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SET_PAGE'; pageIndex: number }
  | { type: 'SET_FILTER'; status: ReportStatus | undefined }
  | { type: 'ACTION_START'; rowId: string }
  | { type: 'ACTION_DONE'; rowId: string; patch: Partial<ReportedPostRow> }
  | { type: 'ACTION_ERROR'; rowId: string };

function reducer(state: QueueState, action: QueueAction): QueueState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, rows: action.rows, totalCount: action.totalCount };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.error };
    case 'SET_PAGE':
      return { ...state, pageIndex: action.pageIndex };
    case 'SET_FILTER':
      return { ...state, statusFilter: action.status, pageIndex: 0 };
    case 'ACTION_START':
      return { ...state, actionPending: { ...state.actionPending, [action.rowId]: true } };
    case 'ACTION_DONE': {
      const pending = { ...state.actionPending };
      delete pending[action.rowId];
      const rows = state.rows.map((r) =>
        r.id === action.rowId ? { ...r, ...action.patch } : r
      );
      return { ...state, actionPending: pending, rows };
    }
    case 'ACTION_ERROR': {
      const pending = { ...state.actionPending };
      delete pending[action.rowId];
      return { ...state, actionPending: pending };
    }
  }
}

const initialState: QueueState = {
  rows: [],
  totalCount: 0,
  pageIndex: 0,
  pageSize: 20,
  statusFilter: 'Pending',
  loading: false,
  error: null,
  actionPending: {},
};

function StatusBadge({ status, outcome }: { status: ReportStatus; outcome?: ReportOutcome | null }) {
  if (status === 'Resolved') {
    const label = outcome ?? 'Resolved';
    const colour =
      outcome === 'Deleted'
        ? 'bg-red-950 text-red-400 border-red-800'
        : outcome === 'Hidden'
          ? 'bg-amber-950 text-amber-400 border-amber-800'
          : 'bg-zinc-800 text-zinc-400 border-zinc-700';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${colour}`}>
        <CheckCircle size={10} aria-hidden="true" />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-violet-800 bg-violet-950 text-violet-400 text-xs font-medium">
      <AlertTriangle size={10} aria-hidden="true" />
      Pending
    </span>
  );
}

function PostCell({ row }: { row: ReportedPostRow }) {
  const [expanded, setExpanded] = useState(false);
  const summary = row.postSummary ?? null;

  if (summary === null) {
    return (
      <>
        <p className="text-zinc-600 text-sm italic">[post deleted]</p>
        <p className="text-zinc-600 text-xs mt-1">ID {row.journalId}</p>
      </>
    );
  }

  return (
    <>
      {row.postTitle && (
        <p className="text-zinc-200 text-sm font-medium mb-0.5 truncate max-w-[220px]">
          {row.postTitle}
        </p>
      )}
      {row.postAuthor && (
        <p className="text-zinc-400 text-xs font-medium mb-0.5 truncate max-w-[220px]">
          {row.postAuthor}
        </p>
      )}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        title={summary}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse post summary' : 'Expand post summary'}
        className={[
          'text-zinc-300 text-sm break-words text-left whitespace-pre-wrap',
          expanded ? '' : 'line-clamp-3',
        ].join(' ')}
      >
        {summary}
        {row.postSummaryTruncated && <span className="text-zinc-500">…</span>}
      </button>
      <p className="text-zinc-600 text-xs mt-1">ID {row.journalId}</p>
    </>
  );
}

interface RowProps {
  row: ReportedPostRow;
  isPending: boolean;
  onResolve: (reportId: string, outcome: ReportOutcome) => void;
  onHide: (journalId: number) => void;
  onUnhide: (journalId: number) => void;
}

function PostRow({ row, isPending, onResolve, onHide, onUnhide }: RowProps) {
  const isResolved = row.status === 'Resolved';
  const isHidden = row.outcome === 'Hidden';

  return (
    <tr className="border-t border-zinc-800 hover:bg-zinc-900/50 transition-colors">
      <td className="px-4 py-3 max-w-xs">
        <PostCell row={row} />
      </td>

      <td className="px-4 py-3 text-sm text-zinc-400">
        <p className="text-xs text-zinc-500 mb-0.5">Reporter</p>
        <p className="font-mono text-xs text-zinc-400 truncate max-w-[120px]">{row.reporterProfileId}</p>
        <p className="text-xs text-zinc-500 mt-1">{row.reason}</p>
      </td>

      <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
        {new Date(row.reportedAt).toLocaleString()}
      </td>

      <td className="px-4 py-3">
        <StatusBadge status={row.status} outcome={row.outcome} />
      </td>

      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2 min-w-[220px]">
          {!isResolved && (
            <>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onResolve(row.id, 'NoAction')}
                className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-50 transition-colors"
                aria-label={`Resolve report ${row.id} with no action`}
              >
                No Action
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onResolve(row.id, 'Hidden')}
                className="min-h-[48px] px-2.5 py-1 rounded bg-amber-950 hover:bg-amber-900 text-amber-300 text-xs disabled:opacity-50 transition-colors"
                aria-label={`Resolve report ${row.id}: hide post`}
              >
                Hide
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onResolve(row.id, 'Deleted')}
                className="min-h-[48px] px-2.5 py-1 rounded bg-red-950 hover:bg-red-900 text-red-300 text-xs disabled:opacity-50 transition-colors"
                aria-label={`Resolve report ${row.id}: delete post (permanent)`}
              >
                Delete
              </button>
            </>
          )}

          {isHidden ? (
            <button
              type="button"
              disabled={isPending}
              onClick={() => onUnhide(row.journalId)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label={`Unhide post ${row.journalId}`}
            >
              <Eye size={12} aria-hidden="true" />
              Unhide
            </button>
          ) : (
            <button
              type="button"
              disabled={isPending || row.outcome === 'Deleted'}
              onClick={() => onHide(row.journalId)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label={`Hide post ${row.journalId}`}
            >
              <EyeOff size={12} aria-hidden="true" />
              Hide
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ReportedPostsTab() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const beginLoad = useLatestRequest();

  const loadPage = useCallback(async () => {
    const isCurrent = beginLoad();
    dispatch({ type: 'FETCH_START' });
    try {
      const result = await getReportedPosts({
        status: state.statusFilter,
        pageIndex: state.pageIndex,
        pageSize: state.pageSize,
      });
      if (!isCurrent()) return;
      dispatch({ type: 'FETCH_SUCCESS', rows: result.items, totalCount: result.totalCount });
    } catch (err) {
      if (!isCurrent()) return;
      dispatch({
        type: 'FETCH_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load reported posts',
      });
    }
  }, [beginLoad, state.statusFilter, state.pageIndex, state.pageSize]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const handleResolve = useCallback(async (reportId: string, outcome: ReportOutcome) => {
    dispatch({ type: 'ACTION_START', rowId: reportId });
    try {
      await resolvePostReport(reportId, outcome);
      dispatch({
        type: 'ACTION_DONE',
        rowId: reportId,
        patch: { status: 'Resolved', outcome, resolvedAt: new Date().toISOString() },
      });
    } catch {
      dispatch({ type: 'ACTION_ERROR', rowId: reportId });
    }
  }, []);

  const handleHide = useCallback(async (journalId: number) => {
    const row = state.rows.find((r) => r.journalId === journalId);
    if (!row) return;
    dispatch({ type: 'ACTION_START', rowId: row.id });
    try {
      await hidePost(journalId);
      dispatch({ type: 'ACTION_DONE', rowId: row.id, patch: { outcome: 'Hidden' } });
    } catch {
      dispatch({ type: 'ACTION_ERROR', rowId: row.id });
    }
  }, [state.rows]);

  const handleUnhide = useCallback(async (journalId: number) => {
    const row = state.rows.find((r) => r.journalId === journalId);
    if (!row) return;
    dispatch({ type: 'ACTION_START', rowId: row.id });
    try {
      await unhidePost(journalId);
      dispatch({ type: 'ACTION_DONE', rowId: row.id, patch: { outcome: null } });
    } catch {
      dispatch({ type: 'ACTION_ERROR', rowId: row.id });
    }
  }, [state.rows]);

  const totalPages = Math.ceil(state.totalCount / state.pageSize);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-zinc-500 text-sm">Review and act on posts other users have reported.</p>
        <div className="flex gap-2" role="group" aria-label="Filter by status">
          {(['Pending', 'Resolved', undefined] as (ReportStatus | undefined)[]).map((s) => {
            const label = s ?? 'All';
            const isActive = state.statusFilter === s;
            return (
              <button
                key={label}
                type="button"
                onClick={() => dispatch({ type: 'SET_FILTER', status: s })}
                className={[
                  'min-h-[48px] px-3 rounded text-sm transition-colors',
                  isActive
                    ? 'bg-violet-700 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200',
                ].join(' ')}
                aria-pressed={isActive}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {state.loading && (
        <div className="flex items-center justify-center py-16 text-zinc-500" role="status" aria-label="Loading">
          <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading reported posts…
        </div>
      )}

      {state.error && !state.loading && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-950 border border-red-800 text-red-300" role="alert">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="font-medium text-sm">Failed to load moderation queue</p>
            <p className="text-xs text-red-400 mt-0.5">{state.error}</p>
            <button
              type="button"
              onClick={() => void loadPage()}
              className="mt-2 text-xs text-red-300 underline hover:text-red-100"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!state.loading && !state.error && state.rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
          <CheckCircle size={40} className="text-zinc-700" aria-hidden="true" />
          <p className="text-sm">No reported posts match this filter.</p>
        </div>
      )}

      {!state.loading && state.rows.length > 0 && (
        <div className="rounded-lg border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" aria-label="Reported posts">
            <thead>
              <tr className="bg-zinc-900 text-zinc-400 text-left text-xs uppercase tracking-wide">
                <th scope="col" className="px-4 py-3 font-medium">Post</th>
                <th scope="col" className="px-4 py-3 font-medium">Reporter / Reason</th>
                <th scope="col" className="px-4 py-3 font-medium">Reported At</th>
                <th scope="col" className="px-4 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((row) => (
                <PostRow
                  key={row.id}
                  row={row}
                  isPending={state.actionPending[row.id] === true}
                  onResolve={handleResolve}
                  onHide={handleHide}
                  onUnhide={handleUnhide}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-zinc-500">
          <span>
            {state.pageIndex * state.pageSize + 1}–
            {Math.min((state.pageIndex + 1) * state.pageSize, state.totalCount)} of{' '}
            {state.totalCount}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={state.pageIndex === 0}
              onClick={() => dispatch({ type: 'SET_PAGE', pageIndex: state.pageIndex - 1 })}
              className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} aria-hidden="true" />
              Prev
            </button>
            <button
              type="button"
              disabled={state.pageIndex >= totalPages - 1}
              onClick={() => dispatch({ type: 'SET_PAGE', pageIndex: state.pageIndex + 1 })}
              className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label="Next page"
            >
              Next
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// All-posts tab (16414) — ported from ForSuccessFrontend by work item 16532
// ---------------------------------------------------------------------------

const STATE_FILTERS: PostModerationState[] = ['all', 'visible', 'hidden', 'removed'];
const ALL_PAGE_SIZE = 20;

const PRODUCER_NAMES = [
  'BoardMilestone',
  'Comeback',
  'CohortPulse',
  'CategoryHeartbeat',
  'WeekInReview',
  'QuestionOfDay',
  'WinDetected',
  'CheckInInvite',
  'AgentProgress',
] as const;

/** `any` = no producer filter, `user` = user posts only, otherwise a producer name. */
type SourceFilter = 'any' | 'user' | (typeof PRODUCER_NAMES)[number];

function parseSourceFilter(value: string | null): SourceFilter {
  if (value === 'user') return 'user';
  return PRODUCER_NAMES.find((n) => n.toLowerCase() === value?.toLowerCase()) ?? 'any';
}

function sourceLabel(row: ModeratedPostRow): string {
  return row.producer ? `System: ${row.producer}` : 'User post';
}

const STATE_LABEL: Record<PostModerationState, string> = {
  all: 'All',
  visible: 'Visible',
  hidden: 'Hidden',
  removed: 'Removed',
};

function rowState(row: ModeratedPostRow): 'Removed' | 'Hidden' | 'Visible' {
  if (row.isDeleted) return 'Removed';
  if (row.isHidden) return 'Hidden';
  return 'Visible';
}

const ROW_STATE_BADGE: Record<'Removed' | 'Hidden' | 'Visible', string> = {
  Removed: 'bg-red-950 text-red-400 border-red-800',
  Hidden: 'bg-amber-950 text-amber-400 border-amber-800',
  Visible: 'bg-emerald-950 text-emerald-400 border-emerald-800',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function AllPostsTab() {
  const [rows, setRows] = useState<ModeratedPostRow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<PostModerationState>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(() =>
    parseSourceFilter(searchParams.get('producer')),
  );
  const journalIdParam = searchParams.get('journalId');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Only the newest request may write state: a slow superseded response (an unfiltered
  // list landing after the filtered one) would otherwise show rows the filter excludes.
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setIsLoading(true);
    try {
      const result = await getModerationQueue({
        state: filter,
        producer: sourceFilter,
        page,
        pageSize: ALL_PAGE_SIZE,
      });
      if (requestId !== latestRequest.current) return;
      setRows(result.items);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
      setError(null);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setError(err instanceof Error ? err.message : 'Could not load the moderation queue.');
    } finally {
      if (requestId === latestRequest.current) setIsLoading(false);
    }
  }, [filter, sourceFilter, page]);

  useEffect(() => {
    // Intentional data-fetch on mount / filter / page change, same pattern as
    // AppContent's loading-timeout effect in App.tsx. The lint rule traces through
    // `load`'s own synchronous setIsLoading(true) at its top, so the disable is on
    // this call, not a raw setState here.
    void load(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [load]);

  const handleFilterChange = (next: PostModerationState) => {
    setFilter(next);
    setPage(0);
    setRows([]);
  };

  const handleSourceChange = (next: SourceFilter) => {
    setSourceFilter(next);
    setPage(0);
    setRows([]);
  };

  const clearJournalId = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('journalId');
    setSearchParams(next, { replace: true });
  };

  // The API has no journalId filter; the detail page only links to a producer's newest
  // entries, which sit on page 0, so narrowing the fetched page is enough.
  const visibleRows = journalIdParam
    ? rows.filter((r) => String(r.journalId) === journalIdParam)
    : rows;

  async function handleHide(row: ModeratedPostRow) {
    if (!window.confirm(`Hide "${row.title}"? Readers will no longer see this post.`)) return;
    setBusyId(row.journalId);
    setActionError(null);
    try {
      await hidePost(row.journalId);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not hide the post.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnhide(row: ModeratedPostRow) {
    if (!window.confirm(`Unhide "${row.title}"? It will reappear in the feed.`)) return;
    setBusyId(row.journalId);
    setActionError(null);
    try {
      await unhidePost(row.journalId);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not unhide the post.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRemove(row: ModeratedPostRow) {
    if (!window.confirm(`Remove "${row.title}"? This cannot be undone from this queue.`)) return;
    setBusyId(row.journalId);
    setActionError(null);
    try {
      await removePost(row.journalId);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not remove the post.');
    } finally {
      setBusyId(null);
    }
  }

  const pageCount = Math.max(1, Math.ceil(totalCount / ALL_PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-zinc-500 text-sm">Every user-authored feed post, filterable by moderation state.</p>
        <button
          type="button"
          onClick={() => void load()}
          disabled={isLoading}
          aria-label="Refresh queue"
          className="min-h-[48px] inline-flex items-center gap-1.5 rounded bg-zinc-800 px-3 text-xs text-zinc-200 hover:bg-zinc-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter by moderation state">
        {STATE_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={filter === s}
            onClick={() => handleFilterChange(s)}
            className={[
              'min-h-[48px] rounded-full border px-3 text-xs transition-colors',
              filter === s
                ? 'border-violet-500 bg-violet-950 text-violet-300'
                : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600',
            ].join(' ')}
          >
            {STATE_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="source-filter" className="text-xs text-zinc-400">Source</label>
        <select
          id="source-filter"
          aria-label="Filter by source"
          value={sourceFilter}
          onChange={(e) => handleSourceChange(parseSourceFilter(e.target.value))}
          className="min-h-[48px] rounded border border-zinc-700 bg-zinc-900 px-2 text-xs text-zinc-200"
        >
          <option value="any">All sources</option>
          <option value="user">User posts</option>
          {PRODUCER_NAMES.map((n) => (
            <option key={n} value={n}>{`System: ${n}`}</option>
          ))}
        </select>
      </div>

      {journalIdParam && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-violet-800 bg-violet-950 p-3 text-sm text-violet-200" role="status">
          <span>
            Showing entry #{journalIdParam}
            {!isLoading && !error && visibleRows.length === 0 && ' - it is not on the loaded page of this queue.'}
          </span>
          <button
            type="button"
            onClick={clearJournalId}
            aria-label="Clear entry filter"
            className="min-h-[48px] rounded border border-violet-700 px-2.5 text-xs text-violet-200 hover:border-violet-500 transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {actionError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-300" role="status">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{actionError}</span>
        </div>
      )}

      {isLoading && rows.length === 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin text-violet-400" aria-hidden="true" /> Loading queue…
        </div>
      )}

      {!isLoading && rows.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
          <CheckCircle size={40} className="text-zinc-700" aria-hidden="true" />
          <p className="text-sm">No posts match this filter.</p>
        </div>
      )}

      {visibleRows.length > 0 && (
        <ul className="space-y-3">
          {visibleRows.map((row) => {
            const state = rowState(row);
            const isBusy = busyId === row.journalId;

            return (
              <li key={row.journalId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${ROW_STATE_BADGE[state]}`}>
                    {state}
                  </span>
                  <span className="text-[11px] text-zinc-500">
                    Source: <span data-testid="post-source" className="text-zinc-300">{sourceLabel(row)}</span>
                  </span>
                </div>
                <h3 className="mt-1.5 truncate text-sm font-semibold text-zinc-100">{row.title}</h3>
                <p className="mt-0.5 line-clamp-2 text-xs text-zinc-400">{row.summary}</p>
                <p className="mt-1 text-[11px] text-zinc-500">
                  Posted {formatDate(row.dateCreated)} · author {row.profileId} · ID {row.journalId}
                </p>

                {state !== 'Removed' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {state === 'Visible' && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => void handleHide(row)}
                        className="min-h-[48px] inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50 transition-colors"
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />}
                        Hide
                      </button>
                    )}
                    {state === 'Hidden' && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => void handleUnhide(row)}
                        className="min-h-[48px] inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50 transition-colors"
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Eye className="h-3.5 w-3.5" aria-hidden="true" />}
                        Unhide
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => void handleRemove(row)}
                      className="min-h-[48px] inline-flex items-center gap-1 rounded bg-red-900 px-2.5 py-1 text-xs font-medium text-red-200 hover:bg-red-800 disabled:opacity-50 transition-colors"
                    >
                      {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />}
                      Remove
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {totalCount > 0 && (
        <div className="flex items-center justify-between text-sm text-zinc-500">
          <span>Page {page + 1} of {pageCount} · {totalCount} post{totalCount === 1 ? '' : 's'}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0 || isLoading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="Previous page"
              className="min-h-[48px] inline-flex items-center gap-1 rounded border border-zinc-700 px-3 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50 transition-colors"
            >
              <ChevronLeft size={14} aria-hidden="true" /> Prev
            </button>
            <button
              type="button"
              disabled={!hasMore || isLoading}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
              className="min-h-[48px] inline-flex items-center gap-1 rounded border border-zinc-700 px-3 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50 transition-colors"
            >
              Next <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page — tab switcher
// ---------------------------------------------------------------------------

export function PostModerationQueuePage() {
  const [searchParams] = useSearchParams();
  // A deep link from the journal detail page carries producer/journalId for the All posts tab.
  const [tab, setTab] = useState<Tab>(() =>
    searchParams.has('producer') || searchParams.has('journalId') ? 'all' : 'reported',
  );

  return (
    <main className="flex-1 p-6 overflow-y-auto space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-zinc-100">Post Moderation</h1>
      </div>

      <div className="flex gap-2 border-b border-zinc-800" role="tablist" aria-label="Post moderation view">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'reported'}
          onClick={() => setTab('reported')}
          className={[
            'min-h-[48px] px-3 text-sm border-b-2 -mb-px transition-colors',
            tab === 'reported'
              ? 'border-violet-500 text-zinc-100'
              : 'border-transparent text-zinc-500 hover:text-zinc-300',
          ].join(' ')}
        >
          Reported
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'all'}
          onClick={() => setTab('all')}
          className={[
            'min-h-[48px] px-3 text-sm border-b-2 -mb-px transition-colors',
            tab === 'all'
              ? 'border-violet-500 text-zinc-100'
              : 'border-transparent text-zinc-500 hover:text-zinc-300',
          ].join(' ')}
        >
          All posts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'held'}
          onClick={() => setTab('held')}
          className={[
            'min-h-[48px] px-3 text-sm border-b-2 -mb-px transition-colors',
            tab === 'held'
              ? 'border-violet-500 text-zinc-100'
              : 'border-transparent text-zinc-500 hover:text-zinc-300',
          ].join(' ')}
        >
          Held wins
        </button>
      </div>

      {tab === 'reported' && <ReportedPostsTab />}
      {tab === 'all' && <AllPostsTab />}
      {tab === 'held' && <HeldWinsPanel />}
    </main>
  );
}
